import * as React from 'react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Field, FieldDescription, FieldLabel } from '@/components/ui/field';
import { SearchIcon } from 'lucide-react';

const FormFieldErrorText = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    role="alert"
    aria-live="polite"
    className={cn(
      'c-form-field-error-text',
      // styles
      'mb-0 text-xs text-danger',
      className
    )}
    {...props}
  />
));
FormFieldErrorText.displayName = 'FormFieldErrorText';

const FormField = React.forwardRef(({ className, id, label, children, hasError, errorText = 'Campo inválido', description = '', type, endIcon: EndIcon, ...props }, ref) => {
  const compId = React.useId();

  // Verifica se o inputElement é React.Element e se tem a prop 'disabled'
  const isDisabled = React.Children.count(children) > 0 ? React.isValidElement(children) && children.props.disabled : props.disabled; // Se não tem children, assume que 'disabled' viria via props para o Input padrão
  const IconToRender = EndIcon || (type === 'search' ? SearchIcon : null);
  const hasEndIcon = !!IconToRender;

  // Se 'children' não for fornecido, tentamos renderizar um Input padrão.
  // Isso é flexível: você pode passar um Input, um Textarea ou outro componente.
  const inputElement = children ? (
    React.Children.only(children) // Garante que apenas um filho é passado
  ) : (
    <div className="relative">
      <Input
        id={id ?? compId}
        aria-invalid={Boolean(hasError) || Boolean(props['aria-invalid'])}
        endIcon={hasEndIcon}
        className="peer"
        type={type}
        {...props}
      />
      {hasEndIcon && (
        <div className="pointer-events-none absolute inset-y-0 end-0 flex items-center justify-center pe-3 text-neutral peer-disabled:opacity-50 [&_svg]:size-4">
          <IconToRender aria-hidden="true" />
        </div>
      )}
    </div>
  );

  return (
    <Field
      className={cn('c-form-field', isDisabled && '[&>label]:cursor-not-allowed [&>label]:opacity-50', className)}
      ref={ref}
    >
      {label && <FieldLabel htmlFor={id ?? compId}>{label}</FieldLabel>}

      {inputElement}

      {description && <FieldDescription>{description}</FieldDescription>}

      {hasError && <FormFieldErrorText>{errorText}</FormFieldErrorText>}
    </Field>
  );
});
FormField.displayName = 'FormField';

export { FormField, FormFieldErrorText };
